import { cn } from '@/lib/utils'
import { useTypewriter } from '@/hooks/use-typewriter'
import { LogoMark } from '@/components/generals/logo'

const QUESTION = 'Is it safe to invest $500 this month?'

const REPLY = [
    'Yes, with room to spare.',
    'Once rent, bills and your savings goal are covered, $1,240 is free this month.',
    'Putting in $500 still leaves you a $740 buffer.',
    'My suggestion: $350 into your index fund, $150 into your emergency fund.'
]

interface ChatPreviewProps {
    active: boolean
}

export const TypingDots = () => {
    return (
        <span className='motion-typing inline-flex items-center gap-1 py-1' aria-label='Verdane is typing'>
            <span className='size-1.5 rounded-full bg-cream/70' />
            <span className='size-1.5 rounded-full bg-cream/70' />
            <span className='size-1.5 rounded-full bg-cream/70' />
        </span>
    )
}

/** A question bubble and an AI reply that types itself out line by line. */
export const ChatPreview = ({ active }: ChatPreviewProps) => {
    const { lines, started, done, activeLine } = useTypewriter(REPLY, active, { startDelay: 1500, speed: 17 })

    return (
        <div className='flex flex-col gap-3'>
            <p className='sr-only'>
                Question: {QUESTION} Answer: {REPLY.join(' ')}
            </p>

            <div
                aria-hidden='true'
                className={cn(
                    'ml-auto max-w-[86%] rounded-[20px] rounded-br-md bg-cream px-4 py-3 text-[13.5px] leading-snug text-black shadow-[0_20px_40px_-20px_rgba(0,0,0,0.8)] transition-all duration-700 ease-cinema',
                    active ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
                )}
            >
                {QUESTION}
            </div>

            <div
                aria-hidden='true'
                className={cn(
                    'glass-dark mr-auto w-[94%] rounded-[20px] rounded-bl-md px-4 py-3.5 transition-all delay-500 duration-700 ease-cinema',
                    active ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
                )}
            >
                <div className='flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-cream/55'>
                    <LogoMark className='size-3.5 text-cream' />
                    Verdane AI
                </div>

                <div className='mt-2.5 grid text-[13.5px] leading-relaxed'>
                    <div className='invisible col-start-1 row-start-1 space-y-1.5'>
                        {REPLY.map(line => <p key={line}>{line}</p>)}
                    </div>

                    <div className='col-start-1 row-start-1 space-y-1.5 text-cream/80'>
                        {!started && <TypingDots />}

                        {started && lines.map((line, index) => line && (
                            <p key={index} className={cn(index === 0 && 'font-medium text-cream')}>
                                {line}

                                {!done && index === activeLine && (
                                    <span className='motion-caret ml-0.5 inline-block h-3.5 w-px translate-y-0.5 bg-citron' />
                                )}
                            </p>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
